import { useCallback, useEffect, useState } from 'react'
import {
  FetchError,
  UserMessageError,
  useGetTaskDelegationQuery,
  useReplaceTaskDelegationMutation
} from 'prove-common'
import {
  delegationToDraft,
  draftToDelegationReplacement,
  type TaskDelegationDraft
} from '../../utils/taskDelegationAdapters'
import {
  validateDelegations,
  type DelegationNode
} from '../../utils/taskDelegationUtils'

type SavedDraft = TaskDelegationDraft & { etag: string }

/** Owns the editable snapshot and the ETag it was based on, including conflict recovery. */
export const useDelegationDraft = (
  taskId: number,
  refetchTask: () => Promise<unknown>,
  onDirtyChange: (isDirty: boolean) => void
) => {
  const query = useGetTaskDelegationQuery(taskId)
  const replace = useReplaceTaskDelegationMutation()
  const [nodes, setDraftNodes] = useState<DelegationNode[]>([])
  const [saved, setSaved] = useState<SavedDraft>()
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<unknown>()
  const [hasConflict, setHasConflict] = useState(false)
  const [savedBranchEtags, setSavedBranchEtags] = useState<
    Record<string, string>
  >({})
  const isDirty =
    saved != null && JSON.stringify(nodes) !== JSON.stringify(saved.nodes)
  const validation = validateDelegations(nodes)

  // Stable identity is needed by the query-to-draft synchronization effect.
  const initialize = useCallback(
    (draft: TaskDelegationDraft, etag: string): void => {
      setDraftNodes(draft.nodes)
      setSaved({ ...draft, etag })
      setSaveError(undefined)
      setHasConflict(false)
    },
    []
  )
  const discard = (): void => {
    if (saved != null) {
      setDraftNodes(saved.nodes)
      setSaveError(undefined)
      setHasConflict(false)
    }
  }
  const reload = async (): Promise<void> => {
    try {
      const result = await query.refetch()
      if (result.isError || result.data == null) {
        throw new Error('Missing refreshed delegation response')
      }
      setSavedBranchEtags(result.data.delegation.branchEtags ?? {})
      initialize(delegationToDraft(result.data.delegation), result.data.etag)
    } catch {
      setSaveError(
        new UserMessageError(
          'Kunne ikke laste siste lagrede fordeling. Utkastet er beholdt. Prøv igjen.'
        )
      )
    }
  }

  useEffect(() => {
    if (query.data != null && !isDirty) {
      setSavedBranchEtags(query.data.delegation.branchEtags ?? {})
      initialize(delegationToDraft(query.data.delegation), query.data.etag)
    }
  }, [query.data, isDirty, initialize])
  useEffect(() => {
    onDirtyChange(isDirty)
    return () => onDirtyChange(false)
  }, [isDirty, onDirtyChange])

  const save = async (): Promise<void> => {
    if (isSaving || !validation.isValid || hasConflict || saved == null) return
    setIsSaving(true)
    setSaveError(undefined)
    try {
      const body = draftToDelegationReplacement({
        plannedQuantity: saved.plannedQuantity,
        nodes
      })
      const previous = draftToDelegationReplacement(saved).entries
      const divisions = new Set(
        [...body.entries, ...previous].map((entry) => entry.path[0])
      )
      const changed = [...divisions].filter((division) => {
        const normalize = (entries: typeof previous) =>
          entries
            .filter((entry) => entry.path[0] === division)
            .map(
              (entry) =>
                `${JSON.stringify(entry.path)}:${entry.plannedQuantity}`
            )
            .sort()
        return (
          JSON.stringify(normalize(body.entries)) !==
          JSON.stringify(normalize(previous))
        )
      })
      const branchUpdate =
        changed.length > 0 &&
        changed.every(
          (division) =>
            savedBranchEtags[division] &&
            body.entries.some(
              (entry) => entry.path.length === 1 && entry.path[0] === division
            )
        )
      const result = await replace.mutateAsync({
        taskId,
        etag: saved.etag,
        body: branchUpdate
          ? {
              entries: body.entries.filter((entry) =>
                changed.includes(entry.path[0])
              ),
              branchEtags: Object.fromEntries(
                changed.map((division) => [
                  division,
                  savedBranchEtags[division]
                ])
              )
            }
          : body
      })
      setSavedBranchEtags(result.delegation.branchEtags ?? {})
      initialize(delegationToDraft(result.delegation), result.etag)
      await refetchTask()
    } catch (caught) {
      setSaveError(caught)
      if (caught instanceof FetchError && caught.status === 412) {
        setHasConflict(true)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const setNodes = (updated: DelegationNode[]): void => {
    const access = query.data?.delegation
    if (access?.canManage !== false || saved == null) {
      setDraftNodes(updated)
      return
    }
    const allowed = new Set(access.redistributableDivisions ?? [])
    // UI totals may be recomputed while editing leaves. Preserve every incoming
    // allocation and every other division exactly as saved, just as the server requires.
    setDraftNodes(
      saved.nodes.map((original) => {
        if (!allowed.has(original.ownerId.toString())) return original
        const changed = updated.find(
          (node) => node.ownerId.toString() === original.ownerId.toString()
        )
        return { ...original, children: changed?.children ?? [] }
      })
    )
  }

  return {
    query,
    nodes,
    setNodes,
    plannedQuantity: saved?.plannedQuantity,
    isDirty,
    isSaving,
    saveError,
    hasConflict,
    validation,
    save,
    discard,
    reload
  }
}
